import { useState, useEffect, useRef } from "react";
import { Flag, Ban, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_REASONS = [
  "스팸/광고",
  "욕설/비방",
  "음란/부적절한 내용",
  "사기/금전 요구",
  "사칭/허위 프로필",
  "기타",
];

/**
 * 모든 대화창에 공통으로 들어가는 신고/차단 액션.
 * Props:
 *  - otherId (상대방 user id) — 필수
 *  - otherName (상대방 이름)
 *  - roomId (선택, 신고 시 참고용)
 *  - size: "sm" | "md"
 */
export default function ChatSafetyActions({ otherId, otherName = "상대방", roomId = null, size = "md" }) {
  const { token } = useAuth();
  const [blocked, setBlocked] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (!token || !otherId) return undefined;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/users/${otherId}/relationship`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && mounted.current) setBlocked((await res.json()).blocked);
      } catch { /* ignore */ }
    })();
    return () => { mounted.current = false; };
  }, [token, otherId]);

  const toggleBlock = async () => {
    if (!otherId) return;
    try {
      const res = await fetch(`${API_URL}/api/users/${otherId}/block`, {
        method: blocked ? "DELETE" : "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setBlocked(!blocked);
        toast.success(d.message || (blocked ? "차단 해제되었습니다." : "차단되었습니다."));
      } else {
        toast.error(d.detail || "처리에 실패했습니다.");
      }
    } catch {
      toast.error("처리 중 오류가 발생했습니다.");
    }
  };

  const submitReport = async () => {
    if (!reason) { toast.error("신고 사유를 선택해주세요."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/users/${otherId}/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason, detail, room_id: roomId }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(d.message || "신고가 접수되었습니다.");
        setShowReport(false); setReason(""); setDetail("");
      } else {
        toast.error(d.detail || "신고에 실패했습니다.");
      }
    } catch {
      toast.error("신고 처리 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const pad = size === "sm" ? "px-2 py-1" : "px-2.5 py-1.5";
  const icon = size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5";

  return (
    <>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => setShowReport(true)}
          className={`flex items-center gap-1 ${pad} rounded-full text-amber-600 hover:bg-amber-50 border border-amber-200 text-xs font-medium transition-colors`}
          data-testid="chat-report-btn"
        >
          <Flag className={icon} />
          <span className="hidden sm:inline">신고</span>
        </button>
        <button
          onClick={toggleBlock}
          className={`flex items-center gap-1 ${pad} rounded-full text-xs font-medium border transition-colors ${
            blocked ? "text-stone-600 border-stone-300 hover:bg-stone-50" : "text-red-600 border-red-200 hover:bg-red-50"
          }`}
          data-testid="chat-block-btn"
        >
          <Ban className={icon} />
          <span className="hidden sm:inline">{blocked ? "차단 해제" : "차단"}</span>
        </button>
      </div>

      {showReport && (
        <div className="fixed inset-0 bg-black/50 z-[120] flex items-center justify-center p-4" onClick={() => setShowReport(false)} data-testid="report-modal-overlay">
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="report-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
              <div className="flex items-center gap-2">
                <Flag className="w-5 h-5 text-amber-500" />
                <h3 className="font-bold text-stone-800">신고하기</h3>
              </div>
              <button onClick={() => setShowReport(false)} className="p-1.5 hover:bg-stone-100 rounded-lg" aria-label="닫기">
                <X className="w-4 h-4 text-stone-500" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-stone-500"><span className="font-semibold text-stone-700">{otherName}</span> 님을 신고하는 사유를 선택해주세요.</p>
              <div className="space-y-2">
                {REPORT_REASONS.map((r) => (
                  <button
                    key={r}
                    onClick={() => setReason(r)}
                    className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm transition-colors ${
                      reason === r ? "border-burgundy-400 bg-burgundy-50 text-burgundy-700 font-semibold" : "border-stone-200 text-stone-600 hover:bg-stone-50"
                    }`}
                    data-testid={`report-reason-${r}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <textarea
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                placeholder="상세 내용 (선택)"
                rows={3}
                className="w-full px-3 py-2.5 bg-stone-50 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 resize-none"
                data-testid="report-detail-input"
              />
            </div>
            <div className="p-5 pt-0 flex gap-3">
              <button onClick={() => setShowReport(false)} className="flex-1 py-2.5 rounded-xl border border-stone-300 text-stone-600 text-sm font-medium hover:bg-stone-50" data-testid="report-cancel-btn">
                취소
              </button>
              <button
                onClick={submitReport}
                disabled={submitting || !reason}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-burgundy-500 to-burgundy-700 text-white text-sm font-semibold disabled:opacity-50 shadow-md hover:shadow-lg transition-all"
                data-testid="report-submit-btn"
              >
                {submitting ? "접수 중..." : "신고 접수"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
